import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiCheckCircle, FiArrowRight, FiBookOpen } from 'react-icons/fi';
import { calculateQualification } from '../data/quizData';

export default function ResultStep({ answers }) {
  const [result, setResult] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setResult(calculateQualification(answers));
      setIsAnalyzing(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, [answers]);

  if (isAnalyzing) {
    return (
      <div className="w-full flex flex-col items-center justify-center min-h-[60vh]">
        <div className="relative w-20 h-20 mb-8">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
            className="absolute inset-0 border-[3px] border-zinc-200 border-t-amber-500 rounded-full"
          />
          <div className="absolute inset-0 flex items-center justify-center">
             <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-[#1A1A1A]">Analyzing your profile...</h2>
        <p className="text-zinc-500 mt-2 font-medium">Crafting your personalized roadmap</p>
      </div>
    );
  }

  const { isQualified } = result;

  return (
    <div className="w-full max-w-xl mx-auto flex flex-col items-center justify-center min-h-[60vh] text-center">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", bounce: 0.5 }}
      >
        <div className={`inline-flex items-center justify-center w-24 h-24 rounded-full mb-8 shadow-2xl
          ${isQualified ? 'bg-green-500 text-white shadow-green-500/20' : 'bg-amber-500 text-white shadow-amber-500/20'}`}
        >
          <SafeIcon icon={isQualified ? FiCheckCircle : FiBookOpen} className="text-5xl" />
        </div>
      </motion.div>

      <motion.h2 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="text-3xl md:text-4xl font-extrabold text-[#1A1A1A] mb-6 tracking-tight"
      >
        {isQualified 
          ? "You qualify for a strategy call." 
          : "Your growth starts here."}
      </motion.h2>

      <motion.p 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="text-lg text-zinc-500 mb-10 leading-relaxed font-medium"
      >
        {isQualified 
          ? "Excellent fit. Based on your answers, our framework is perfectly aligned with your current goals. Let's schedule your 1-on-1."
          : "While you're not ready for a strategy call just yet, our MaDaSkool curriculum is built specifically to help you bridge this gap."}
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="w-full"
      >
        {isQualified ? (
          <a
            href="https://lunacal.com/booking"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center justify-center py-5 px-6 rounded-2xl bg-[#1A1A1A] text-white font-bold text-xl hover:bg-zinc-800 transition-all duration-300 shadow-2xl shadow-zinc-950/20 active:scale-[0.98]"
          >
            Continue to Booking
            <SafeIcon icon={FiArrowRight} className="ml-3 text-2xl" />
          </a>
        ) : (
          <a
            href="https://www.madaskool.com"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center justify-center py-5 px-6 rounded-2xl bg-white text-[#1A1A1A] font-bold text-xl border-2 border-zinc-200 hover:border-amber-500 hover:text-amber-600 transition-all duration-300 shadow-xl shadow-zinc-200/50 active:scale-[0.98]"
          >
            Join MaDaSkool
            <SafeIcon icon={FiArrowRight} className="ml-3 text-2xl" />
          </a>
        )}
      </motion.div>
    </div>
  );
}